import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useCreatePage } from "../../../lib/hooks/use-kuvio";
import { PAGE_SURFACES, PAGE_STATUSES, pageSurfaceMeta, pageStatusMeta } from "../../lib/page-meta";
import type { LibraryNode } from "../../../lib/types";

interface NewPageModalProps {
  open: boolean;
  onClose: () => void;
  /** Parent node the new page is created under. */
  parentId: string;
  onCreated: (node: LibraryNode) => void;
  /**
   * Sprint 18 — frontmatter pre-filled on creation (e.g. an `entities` array
   * pre-linking the page to a task). Merged into the new page's frontmatter.
   */
  initialFrontmatter?: Record<string, unknown>;
}

/**
 * New-page modal (Sprint 5o S5O.18). Name + surface + status + optional body.
 * The body textarea is mostly for tooling; humans typically create an empty
 * draft and write it in the full-page editor.
 *
 * Sprint 18 — an optional `initialFrontmatter` lets a caller create a page that
 * is already linked (e.g. "+ New page" inside a task's expanded area).
 */
export function NewPageModal({ open, onClose, parentId, onCreated, initialFrontmatter }: NewPageModalProps) {
  const create = useCreatePage();
  const [name, setName] = useState("");
  const [surface, setSurface] = useState<string>("spec");
  const [status, setStatus] = useState<string>("draft");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setSurface("spec");
      setStatus("draft");
      setBody("");
      setError(null);
      setTimeout(() => nameRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      nameRef.current?.focus();
      return;
    }
    setError(null);
    try {
      const node = await create.mutateAsync({
        name: trimmed,
        parent_id: parentId,
        surface,
        status,
        body: body.trim().length > 0 ? body : undefined,
        ...(initialFrontmatter ? { frontmatter: initialFrontmatter } : {}),
      });
      onClose();
      onCreated(node);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create page.");
    }
  }

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="new-page-title" style={box}>
        <h2 id="new-page-title" style={titleStyle}>New page</h2>

        <label style={labelStyle} htmlFor="new-page-name">Name</label>
        <input
          id="new-page-name"
          ref={nameRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          placeholder="e.g. Q3 launch plan"
          style={inputStyle}
        />

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="new-page-surface">Surface</label>
            <select id="new-page-surface" value={surface} onChange={(e) => setSurface(e.target.value)} style={inputStyle}>
              {PAGE_SURFACES.map((s) => (
                <option key={s} value={s}>{pageSurfaceMeta(s).label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="new-page-status">Status</label>
            <select id="new-page-status" value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
              {PAGE_STATUSES.map((s) => (
                <option key={s} value={s}>{pageStatusMeta(s).label}</option>
              ))}
            </select>
          </div>
        </div>

        <label style={labelStyle} htmlFor="new-page-body">Body <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional)</span></label>
        <textarea
          id="new-page-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Start writing, or leave empty and edit later…"
          rows={4}
          style={{ ...inputStyle, fontFamily: "var(--font-mono, monospace)", resize: "vertical" }}
        />

        {error && <div style={errorStyle}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...btnBase, background: "transparent", color: "var(--color-text-secondary)" }}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={create.isPending}
            style={{ ...btnBase, background: "var(--color-text-primary)", color: "var(--color-background-primary)", opacity: create.isPending ? 0.7 : 1 }}
          >
            {create.isPending ? "Creating…" : "Create page"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
};

const box: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  maxWidth: 460,
  width: "100%",
  padding: 24,
};

const titleStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  margin: "0 0 16px 0",
  color: "var(--color-text-primary)",
};

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  margin: "10px 0 4px",
};

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  fontSize: 13,
  color: "var(--color-text-primary)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  outline: "none",
  fontFamily: "inherit",
};

const errorStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--c-red-600)",
  marginTop: 12,
};

const btnBase: CSSProperties = {
  fontSize: 13,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  border: "none",
};
